import { useState, useCallback } from 'react';
import {
  Box,
  Stack,
  Button,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tooltip,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, parseISO } from 'date-fns';
import { PotSummaryCard } from '../PotSummaryCard';
import {
  useAddRejectionReason,
  useResolveRejectionReason,
  useRemoveRejectionReason,
} from '../../hooks/useRejectionReasons';
import type {
  PotDetailsDisplayModel,
  PotRejectionReason,
} from '../../types/potDetails.types';

interface RejectionManagementPanelContentProps {
  potDetails: PotDetailsDisplayModel;
  onStatusChange?: () => void;
}

export function RejectionManagementPanelContent({
  potDetails,
  onStatusChange,
}: RejectionManagementPanelContentProps) {
  const [newReason, setNewReason] = useState('');

  const { mutate: addReason, isPending: isAdding } = useAddRejectionReason();

  const { mutate: resolveReason, isPending: isResolving } =
    useResolveRejectionReason();

  const { mutate: removeReason, isPending: isRemoving } =
    useRemoveRejectionReason();

  const isPending = isAdding || isResolving || isRemoving;

  const handleAddReason = useCallback(() => {
    const trimmedReason = newReason.trim();
    if (trimmedReason) {
      addReason(
        { potId: potDetails.id, reason: trimmedReason },
        {
          onSuccess: response => {
            setNewReason('');
            if (response.status !== potDetails.approvals.status) {
              onStatusChange?.();
            }
          },
        }
      );
    }
  }, [
    addReason,
    newReason,
    potDetails.id,
    potDetails.approvals.status,
    onStatusChange,
  ]);

  const handleResolveReason = useCallback(
    (reasonId: string) => {
      resolveReason(
        { potId: potDetails.id, reasonId },
        {
          onSuccess: response => {
            if (response.status !== potDetails.approvals.status) {
              onStatusChange?.();
            }
          },
        }
      );
    },
    [resolveReason, potDetails.id, potDetails.approvals.status, onStatusChange]
  );

  const handleRemoveReason = useCallback(
    (reasonId: string) => {
      removeReason(
        { potId: potDetails.id, reasonId },
        {
          onSuccess: response => {
            if (response.status !== potDetails.approvals.status) {
              onStatusChange?.();
            }
          },
        }
      );
    },
    [removeReason, potDetails.id, potDetails.approvals.status, onStatusChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAddReason();
      }
    },
    [handleAddReason]
  );

  const formatDateTime = (dateString: string) => {
    return format(parseISO(dateString), 'dd MMM yyyy, HH:mm');
  };

  const unresolvedReasons = potDetails.rejectionReasons.filter(
    r => !r.isResolved
  );
  const resolvedReasons = potDetails.rejectionReasons.filter(r => r.isResolved);

  const canAddReason = newReason.trim().length > 0;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {/* Summary Card */}
          <PotSummaryCard
            ownerType={potDetails.owner.ownerType}
            ownerName={potDetails.owner.ownerName}
            period={potDetails.period}
          />

          {/* Status Summary */}
          <Alert
            severity={unresolvedReasons.length > 0 ? 'warning' : 'success'}
            icon={false}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                size="small"
                color={unresolvedReasons.length > 0 ? 'warning' : 'success'}
                label={`${unresolvedReasons.length} unresolved`}
              />
              {resolvedReasons.length > 0 && (
                <Chip
                  size="small"
                  color="default"
                  label={`${resolvedReasons.length} resolved`}
                />
              )}
            </Stack>
          </Alert>

          {/* Add New Reason */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Add New Reason
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter a new rejection reason..."
                value={newReason}
                onChange={e => setNewReason(e.target.value)}
                onKeyDown={handleKeyDown}
                slotProps={{
                  htmlInput: {
                    maxLength: 1000,
                    'data-testid': 'management-reason-input',
                  },
                }}
                disabled={isPending}
              />
              <Button
                variant="outlined"
                onClick={handleAddReason}
                disabled={!canAddReason || isPending}
                sx={{ minWidth: 'auto', px: 2 }}
                data-testid="management-add-reason-button"
              >
                {isAdding ? <CircularProgress size={20} /> : <AddIcon />}
              </Button>
            </Box>
          </Box>

          {/* Unresolved Reasons */}
          {unresolvedReasons.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Unresolved Reasons ({unresolvedReasons.length})
              </Typography>
              <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                {unresolvedReasons.map(reason => (
                  <ReasonListItem
                    key={reason.id}
                    reason={reason}
                    onResolve={() => handleResolveReason(reason.id)}
                    onRemove={() => handleRemoveReason(reason.id)}
                    formatDateTime={formatDateTime}
                    disabled={isPending}
                  />
                ))}
              </List>
            </Box>
          )}

          {/* Resolved Reasons */}
          {resolvedReasons.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                color="text.secondary"
              >
                Resolved Reasons ({resolvedReasons.length})
              </Typography>
              <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                {resolvedReasons.map(reason => (
                  <ReasonListItem
                    key={reason.id}
                    reason={reason}
                    onRemove={() => handleRemoveReason(reason.id)}
                    formatDateTime={formatDateTime}
                    disabled={isPending}
                    isResolved
                  />
                ))}
              </List>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Info footer */}
      {unresolvedReasons.length === 0 && (
        <Box
          sx={{
            p: 3,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Alert severity="success" data-testid="all-reasons-resolved-alert">
            All rejection reasons have been resolved. The pot has transitioned
            to Verified status.
          </Alert>
        </Box>
      )}
    </Box>
  );
}

interface ReasonListItemProps {
  reason: PotRejectionReason;
  onResolve?: () => void;
  onRemove: () => void;
  formatDateTime: (date: string) => string;
  disabled: boolean;
  isResolved?: boolean;
}

function ReasonListItem({
  reason,
  onResolve,
  onRemove,
  formatDateTime,
  disabled,
  isResolved = false,
}: ReasonListItemProps) {
  return (
    <ListItem
      sx={{
        textDecoration: isResolved ? 'line-through' : 'none',
        opacity: isResolved ? 0.7 : 1,
      }}
    >
      <ListItemText
        primary={reason.reason}
        secondary={
          <Stack component="span" spacing={0.5}>
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              Added by {reason.createdByName} on{' '}
              {formatDateTime(reason.createdAt)}
            </Typography>
            {isResolved && reason.resolvedAt && (
              <Typography
                component="span"
                variant="caption"
                color="success.main"
              >
                Resolved by {reason.resolvedByName} on{' '}
                {formatDateTime(reason.resolvedAt)}
              </Typography>
            )}
          </Stack>
        }
        primaryTypographyProps={{
          variant: 'body2',
          sx: { textDecoration: isResolved ? 'line-through' : 'none' },
        }}
      />
      <ListItemSecondaryAction>
        <Stack direction="row" spacing={0.5}>
          {!isResolved && onResolve && (
            <Tooltip title="Mark as resolved">
              <IconButton
                edge="end"
                size="small"
                color="success"
                onClick={onResolve}
                disabled={disabled}
                data-testid="management-resolve-button"
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Remove reason">
            <IconButton
              edge="end"
              size="small"
              color="error"
              onClick={onRemove}
              disabled={disabled}
              data-testid="management-remove-button"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </ListItemSecondaryAction>
    </ListItem>
  );
}
